import React from "react";
import Header from "../../header/header.jsx";
import Footer from "../../footer/footer.jsx";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./services_content.css";
import { info, info2 } from "../../../utilities/dataservices.js";
import { useTranslation } from "react-i18next";
import ButtonLinkComponent from "../../buttonLink/buttonLink.jsx";

// Los iconos de tecnologias no traen su nombre (dataservices.js solo guarda
// el archivo de imagen), y algunos como el de Azure no se reconocen a simple
// vista solo con el logo. Este mapa les pone un nombre visible debajo.
// NOTA: "qgbJ08Y16II1jB0TKK7i-350x350.svg" se etiqueto como "Blue Prism" por
// ser el tercer logo de RPA junto a Automation Anywhere y UiPath — confirmar
// que es correcto.
const TOOL_NAMES = {
  "logo_python_1.svg": "Python",
  "Power_BI_1.svg": "Power BI",
  "Power_BI_Icon_1.svg": "Power BI",
  "Azure_logo_1.svg": "Azure",
  "AI_builder_logo_1.svg": "AI Builder",
  "PVA_logo_1.svg": "Power Virtual Agents",
  "automation-anywhere-logo-corporate-two-line-lg.svg": "Automation Anywhere",
  "UiPath-partner-full.svg": "UiPath",
  "qgbJ08Y16II1jB0TKK7i-350x350.svg": "Blue Prism",
  "Power_automate_desktop_1.svg": "Power Automate",
  "AWS_icon.svg": "AWS",
  "Microsoft_Office_SharePoint_(2019present).svg": "SharePoint",
  "google-cloud-icon-1024x823-wiwlyizc.svg": "Google Cloud",
  "react_1.svg": "React",
  "power_apps_logo_1.svg": "Power Apps",
};

const Services_content = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const [service, setService] = useState("");

  // El RefID identifica en Microsoft Bookings desde que servicio especifico
  // (IA, RPA, Cloud, BI, Web/App Dev) llego cada reserva, para saber que
  // pagina desperto el interes del cliente.
  function openBooking() {
    const refId = (service || "TECHSERVICES").toUpperCase();
    const BOOKING_URL = `https://outlook.office365.com/book/G07e86683359a41f58322cab8b2047723@5igsolutions.com/?ismsaljsauthenabled=true&RefID=TECHSERVICES_${refId}`;

    window.open(BOOKING_URL, "_blank", "noopener,noreferrer");
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    setService(params.service);
  }, [params.service]);

  const DynamicImage = ({ src, alt }) => {
    return <img className="image_service" src={src} alt={alt} />;
  };

  const heroMap = useMemo(
  () => ({
    ia: {
      title: [t("HOME_SERVICE_IA_TITLE_1"), t("HOME_SERVICE_IA_TITLE_2")],
      subtitle: t("TECH_SERVICE_IA_SUBTITLE"),
    },
    rpa: {
      title: [t("HOME_SERVICE_RPA_TITLE_1"), t("HOME_SERVICE_RPA_TITLE_2")],
      subtitle: t("TECH_SERVICE_RPA_SUBTITLE"),
    },
    cloud: {
      title: [t("HOME_SERVICE_CLOUD_TITLE")],
      subtitle: t("TECH_SERVICE_CLOUD_SUBTITLE"),
    },
    bi: {
      title: [t("HOME_SERVICE_BI_TITLE_1"), t("HOME_SERVICE_BI_TITLE_2")],
      subtitle: t("TECH_SERVICE_BI_SUBTITLE"),
    },
    "web-app-dev": {
      title: [t("HOME_SERVICE_WEB_DEV_TITLE_1"), t("HOME_SERVICE_WEB_DEV_TITLE_2")],
      subtitle: t("TECH_SERVICE_WEB_SUBTITLE"),
    },
  }),
  [t]
);

  const serviceConfig = {
    ia: { startIdx: 0, endIdx: 3 },
    rpa: { startIdx: 3, endIdx: 5 },
    cloud: { startIdx: 5, endIdx: 7 },
    bi: { startIdx: 7, endIdx: 9 },
    "web-app-dev": { startIdx: 9, endIdx: 11 },
  };

  const currentConfig = serviceConfig[service];
  const heroContent = heroMap[service];

  const serviceItems =
    currentConfig?.startIdx !== undefined
      ? info.slice(currentConfig.startIdx, currentConfig.endIdx)
      : [];

  const findServices = (idService) => {
    const foundService = info2.find((item) => item.id === idService);
    return foundService ? foundService.contentServices : null;
  };

  const services = findServices(params.service);

  const getConcatenatedText = (keys) => {
    return keys.map((key) => t(key)).join(" ");
  };

  return (
    <div className="services-page">
      <Header />

      <main className="service-detail-page">
        {heroContent && (
          <section
            className={`service-hero ${service}`}
            data-service={service}
          >
            <div className="service-hero-overlay" />
            <div className="service-hero-inner">
              <span className="service-hero-chip">{t("TECH_SERVICE_CHIP")}</span>

              <div className="service-hero-copy">
                <h1 className="service-hero-title">
                  {heroContent.title.map((line, idx) => (
                    <span key={idx}>{line}</span>
                  ))}
                </h1>

                <p className="service-hero-subtitle">{heroContent.subtitle}</p>

                <div className="service-hero-actions">
                  <button type="button" className="hero-primary-btn" onClick={openBooking}>
                    {t("HOME_HERO_CTA")}
                  </button>

                  <button
                    type="button"
                    className="hero-secondary-btn"
                    onClick={() => navigate("/techservices")}
                  >
                    {t("TECH_SERVICE_MORE_SOLUTIONS")}
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="service-content-section" aria-labelledby="service-features">
          <div className="service-content-shell">
            {serviceItems.map((item, index) => (
              <article
                key={index}
                className={`content-card ${index % 2 !== 0 ? "reverse" : ""}`}
              >
                <div className="content-card-media">
                  <DynamicImage src={item.img_source} alt={t(item.title)} />
                </div>

                <div className="content-card-body">
                  <h2>{t(item.title)}</h2>
                  <p>{t(item.info)}</p>

                  {item.technologies && (
                    <div className="tools-block">
                      <h3>{t("Tools_That_We_Use")}</h3>
                      <div className="toolsContainter">
                        {Object.values(item.technologies).map((src, idx) => (
                          <div key={idx} className="tool-pill">
                            <img src={`/img/${src}`} alt={TOOL_NAMES[src] || t(item.title)} />
                            <span className="tool-pill-label">{TOOL_NAMES[src] || ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="service-cta-section">
          <div className="service-cta-card">
            <div className="service-cta-copy">
              <h2>{t("TECH_SERVICE_FINAL_CTA_TITLE")}</h2>
              <p>{t("TECH_SERVICE_FINAL_CTA_TEXT")}</p>
            </div>

            <button type="button" className="service-cta-button" onClick={openBooking}>
              {t("HOME_HERO_CTA")}
            </button>
          </div>
        </section>

        {services && (
          <section className="more-services-section">
            <div className="more-services-container">
              <div className="more-services-heading">
                <h2>{t("HOME_SERVICES_TITLE")}</h2>
                <p>{t("TECH_SERVICES_MORE_SERVICES_SUB")}</p>
              </div>

              <div className="more-services-grid">
                <article className="more-service-card">
                  <div className="more-service-top">
                    <div className="moreServicesImgContainer">
                      <img src={`/img/${services[0].img1}.svg`} alt="" />
                    </div>
                    <h3>{getConcatenatedText(services[0].text1)}</h3>
                  </div>
                  <ButtonLinkComponent
                    translateButton={'HOME_SERVICES_BUTTON'}
                    url={services[0].link1}
                    className={"button more-service-link"}
                  />
                </article>

                <article className="more-service-card">
                  <div className="more-service-top">
                    <div className="moreServicesImgContainer">
                      <img src={`/img/${services[0].img2}.svg`} alt="" />
                    </div>
                    <h3>{getConcatenatedText(services[0].text2)}</h3>
                  </div>
                  <ButtonLinkComponent
                    translateButton={'HOME_SERVICES_BUTTON'}
                    url={services[0].link2}
                    className={"button more-service-link"}
                  />
                </article>

                <article className="more-service-card">
                  <div className="more-service-top">
                    <div className="moreServicesImgContainer">
                      <img src={`/img/${services[0].img3}.svg`} alt="" />
                    </div>
                    <h3>{getConcatenatedText(services[0].text3)}</h3>
                  </div>
                  <ButtonLinkComponent
                    translateButton={'HOME_SERVICES_BUTTON'}
                    url={services[0].link3}
                    className={"button more-service-link"}
                  />
                </article>

                <article className="more-service-card">
                  <div className="more-service-top">
                    <div className="moreServicesImgContainer">
                      <img src={`/img/${services[0].img4}.svg`} alt="" />
                    </div>
                    <h3>{getConcatenatedText(services[0].text4)}</h3>
                  </div>
                  <ButtonLinkComponent
                    translateButton={'HOME_SERVICES_BUTTON'}
                    url={services[0].link4}
                    className={"button more-service-link"}
                  />
                </article>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Services_content;